import React from 'react';
import {
  Box, Grid, Card, Stack, Typography, Switch, TextField, Button, IconButton, Chip,
  MenuItem, alpha, useTheme, Divider, Alert,
} from '@mui/material';
import { Plus, Trash2, GitBranch, MessageSquare, CornerDownRight, Rocket, Tag, Link, ArrowLeft, Pencil } from 'lucide-react';
import WaMessageEditor from './WaMessageEditor';

// Canonical seller-onboarding flow — mirrors the backend seed
// (waFlowEngine.js SELLER_ONBOARDING_FLOW). Loaded with one click so the team
// gets the full tap-through funnel ready to enable after deploy.
const SIGNUP = 'https://www.resellerbazzar.in/signup';
const GUIDE = 'https://www.resellerbazzar.in/information/onboarding-process';
const HOME = 'https://www.resellerbazzar.in';
export const SELLER_ONBOARDING_TEMPLATE = {
  id: 'seller_onboarding',
  name: 'Seller Onboarding',
  enabled: false,
  trigger_keywords: ['interested', 'intrested'],
  start_step: 'q_role',
  expire_hours: 48,
  reengage_template: '', // set to an APPROVED Meta template to re-engage stalled leads
  reengage_language: 'en_US',
  reengage_after_hours: 6,
  steps: [
    {
      id: 'q_role',
      body: "🙏 Thanks for reaching out to *Reseller Bazzar*, {{user}}!\n\nWe connect sellers *directly* with buyers across India — no middlemen, and *zero commission* to start. Let's get you set up in under 2 minutes.\n\nFirst, what best describes you?",
      is_final: false,
      buttons: [
        { id: 'role_seller', title: 'Seller', next: 'q_city', add_tag: 'Lead: Seller', set: 'role=seller', keywords: ['seller', 'reseller', 'retailer'] },
        { id: 'role_wholesaler', title: 'Wholesaler', next: 'q_city', add_tag: 'Lead: Wholesaler', set: 'role=wholesaler', keywords: ['wholesaler', 'wholesale'] },
        { id: 'role_manufacturer', title: 'Manufacturer', next: 'q_city', add_tag: 'Lead: Manufacturer', set: 'role=manufacturer', keywords: ['manufacturer', 'manufacture', 'maker', 'factory'] },
      ],
    },
    {
      id: 'q_city',
      body: 'Great 👍 Which *city* are you based in? This helps us connect you with buyers nearby.',
      is_final: false,
      capture: 'city',
      set_contact: 'city',
      tag_prefix: 'City: ',
      next: 'q_category',
      buttons: [],
    },
    {
      id: 'q_category',
      body: 'Perfect. What do you mainly *sell*? Just type your main product category — e.g. Apparel, Electronics, Groceries, Beauty, Home.',
      is_final: false,
      capture: 'category',
      tag_prefix: 'Sells: ',
      next: 'q_online',
      buttons: [],
    },
    {
      id: 'q_online',
      body: 'Almost there 🙌 Are you *already selling online* today — on any website or marketplace (Amazon, Flipkart, Meesho, your own site)?',
      is_final: false,
      buttons: [
        { id: 'online_yes', title: 'Yes, I sell online', next: 'qa_online', add_tag: 'Sells Online', set: 'online=yes', keywords: ['yes', 'online', 'amazon', 'flipkart', 'meesho'] },
        { id: 'online_no', title: 'Not yet', next: 'qa_offline', add_tag: 'New to Online', set: 'online=no', keywords: ['no', 'not yet', 'offline', 'new'] },
      ],
    },
    {
      id: 'qa_online',
      body: 'Love it! 🚀 On *Reseller Bazzar* you keep selling the way you do today — but we put your *{{category}}* in front of buyers near *{{city}}* who are *actively looking to buy*, and you deal with them *directly*.\n\n✅ More buyers, more orders\n✅ *Zero commission* to start\n✅ You own the customer — we just connect you\n\nReady to get connected?',
      is_final: false,
      buttons: [
        { id: 'online_go', title: 'Connect me 🤝', next: 'done', add_tag: 'Lead: Hot', set: '', keywords: ['yes', 'ready', 'sure', 'connect', 'ok', 'okay'] },
      ],
    },
    {
      id: 'qa_offline',
      body: 'No problem at all 🙌 Most of our top sellers started exactly where you are. *Reseller Bazzar* gets your *{{category}}* online in *minutes* — for free — and connects you *directly* with buyers near *{{city}}*.\n\n✅ Set up your shop in minutes\n✅ *Zero commission* to start\n✅ Sell directly to buyers — no middlemen\n\nShall we get you online?',
      is_final: false,
      buttons: [
        { id: 'offline_go', title: 'Get me online 🚀', next: 'done', add_tag: 'Lead: Hot', set: '', keywords: ['yes', 'ready', 'sure', 'online', 'ok', 'okay'] },
      ],
    },
    {
      id: 'done',
      is_final: true,
      add_tag: 'Lead: Qualified',
      body: "You're all set, {{user}}! 🎉\n\nHere's how to go live on *Reseller Bazzar*:\n1️⃣ Create your free account\n2️⃣ Add your *business* & list your *{{category}}*\n3️⃣ Pick a plan and start connecting with buyers near *{{city}}*\n\nTap *Register Now* below — it takes 2 minutes. Reply *HELP* anytime and our team will assist you.",
      buttons: [
        { id: 'signup_link', type: 'url', title: 'Register Now', url: SIGNUP },
        { id: 'guide_link', type: 'url', title: 'How it works', url: GUIDE },
        { id: 'home_link', type: 'url', title: 'Browse Reseller Bazzar', url: HOME },
      ],
    },
  ],
};

export const CUSTOMER_ACQUISITION_TEMPLATE = {
  id: 'customer_acquisition',
  name: 'Customer Acquisition',
  enabled: false,
  trigger_keywords: ['buy wholesale', 'reseller price', 'join reseller', 'acquire'],
  start_step: 'intro',
  expire_hours: 48,
  reengage_template: '',
  reengage_language: 'en_US',
  reengage_after_hours: 6,
  steps: [
    {
      id: 'intro',
      body: "Welcome to *Reseller Bazzar*! 🌟 We connect wholesale buyers directly with verified manufacturers and brands across India with *zero middleman margins*. Let's find you the best suppliers in seconds.\n\nFirst, what describes your role best?",
      is_final: false,
      buttons: [
        { id: 'role_buyer', title: 'Bulk Buyer', next: 'q_category', add_tag: 'Lead: Buyer', set: 'role=buyer', keywords: ['buyer', 'bulk', 'wholesale buyer'] },
        { id: 'role_reseller', title: 'Online Reseller', next: 'q_category', add_tag: 'Lead: Reseller', set: 'role=reseller', keywords: ['reseller', 'online reseller', 'retailer'] },
      ],
    },
    {
      id: 'q_category',
      body: 'Excellent 👍 What *product categories* are you looking to source? (e.g. Apparel, Electronics, Groceries, Beauty, Home Decor)',
      is_final: false,
      capture: 'category',
      tag_prefix: 'Wants: ',
      next: 'q_estimate',
      buttons: [],
    },
    {
      id: 'q_estimate',
      body: 'Got it. What is your estimated *monthly sourcing budget*? This helps us match you with the right suppliers.',
      is_final: false,
      buttons: [
        { id: 'est_low', title: 'Under ₹10,000', next: 'done', add_tag: 'Sourcing: <10k', set: 'sourcing_budget=low', keywords: ['low', '10000', 'under 10k'] },
        { id: 'est_mid', title: '₹10,000 - ₹50,000', next: 'done', add_tag: 'Sourcing: 10k-50k', set: 'sourcing_budget=mid', keywords: ['mid', 'medium', '10k-50k'] },
        { id: 'est_high', title: 'Over ₹50,000', next: 'done', add_tag: 'Sourcing: >50k', set: 'sourcing_budget=high', keywords: ['high', 'bulk buy', 'over 50k'] },
      ],
    },
    {
      id: 'done',
      is_final: true,
      add_tag: 'Lead: Sourcing Hot',
      body: "You're all set! 🎉 We have matched your requirement with top verified suppliers in *{{category}}*.\n\nHere are your next steps:\n1️⃣ Click the link below to browse matching wholesalers.\n2️⃣ Post a Request for Quote (RFQ) to receive direct pricing.\n\nLet's get you the best wholesale prices! 🤝",
      buttons: [
        { id: 'wholesalers_link', type: 'url', title: 'Browse Wholesalers', url: 'https://www.resellerbazzar.in/all-businesses' },
        { id: 'rfq_link', type: 'url', title: 'Post RFQ 📋', url: 'https://www.resellerbazzar.in/rfq' },
      ],
    },
  ],
};

export const GENERAL_ENQUIRY_TEMPLATE = {
  id: 'general_enquiry',
  name: 'General Enquiry',
  enabled: false,
  trigger_keywords: ['enquiry', 'inquiry', 'help', 'support', 'question'],
  start_step: 'intro',
  expire_hours: 24,
  reengage_template: '',
  reengage_language: 'en_US',
  reengage_after_hours: 0,
  steps: [
    {
      id: 'intro',
      body: "Hello! Thank you for reaching out to *Reseller Bazzar*. ❤️ We are dedicated to making your business journey smooth and successful.\n\nHow can we help you today?",
      is_final: false,
      buttons: [
        { id: 'enq_selling', title: 'How to Sell', next: 'selling_help', add_tag: 'Enquiry: Selling', set: 'topic=selling', keywords: ['sell', 'seller', 'become seller', 'merchant'] },
        { id: 'enq_buying', title: 'How to Buy', next: 'buying_help', add_tag: 'Enquiry: Buying', set: 'topic=buying', keywords: ['buy', 'buyer', 'purchase', 'shop'] },
        { id: 'enq_other', title: 'Other Questions', next: 'other_help', add_tag: 'Enquiry: General', set: 'topic=other', keywords: ['other', 'support', 'help', 'scam', 'complaint'] },
      ],
    },
    {
      id: 'selling_help',
      body: "Reseller Bazzar is a 100% commission-free wholesale marketplace! 🚀 You can set up your online store, list products, and connect directly with buyers.\n\nWould you like to read our onboarding guide or request a call back from our advisor?",
      is_final: false,
      buttons: [
        { id: 'guide_link', type: 'url', title: 'Onboarding Guide 📖', url: 'https://www.resellerbazzar.in/information/onboarding-process' },
        { id: 'callback_req', title: 'Request Call Back 📞', next: 'callback_request', add_tag: 'Request: Call Back', set: 'action=callback', keywords: ['call', 'call back', 'phone', 'advisor', 'talk'] },
      ],
    },
    {
      id: 'buying_help',
      body: "Buying is simple and secure on *Reseller Bazzar*. 🛡️ You can search our public catalog, or submit a Request for Quote (RFQ) to receive bulk quotes directly from verified manufacturers.\n\nWhat would you like to do?",
      is_final: false,
      buttons: [
        { id: 'rfq_link', type: 'url', title: 'Post an RFQ 📋', url: 'https://www.resellerbazzar.in/rfq' },
        { id: 'safety_link', type: 'url', title: 'Buyer Safety Guide 🛡️', url: 'https://www.resellerbazzar.in/information/buyer-safety-guide' },
      ],
    },
    {
      id: 'other_help',
      body: 'We are here for you! Please type your question or issue in detail below, and our customer support team will reply personally within 30 minutes. 💬',
      is_final: false,
      capture: 'enquiry_text',
      tag_prefix: 'Question: ',
      next: 'confirm_receipt',
      buttons: [],
    },
    {
      id: 'callback_request',
      body: "We'd love to help you scale your business! 📞 When would you like our business growth expert to call you?",
      is_final: false,
      buttons: [
        { id: 'slot_11am', title: '11:00 AM', next: 'confirm_receipt', add_tag: 'Callback: 11am', set: 'time=11am', keywords: ['11am', '11:00', 'morning'] },
        { id: 'slot_4pm', title: '4:00 PM', next: 'confirm_receipt', add_tag: 'Callback: 4pm', set: 'time=4pm', keywords: ['4pm', '16:00', 'afternoon'] },
      ],
    },
    {
      id: 'confirm_receipt',
      is_final: true,
      add_tag: 'Enquiry: Handled',
      body: "Thank you so much! 🙏 We have received your inquiry.\n\nA Reseller Bazzar support specialist will contact you shortly to guide you. Have a wonderful and profitable day! 🌟",
      buttons: [],
    },
  ],
};

const slug = (n) => `step_${n}`;

const WhatsAppFlowBuilder = ({ flows, setFlows }) => {
  const theme = useTheme();
  const [selectedFlowId, setSelectedFlowId] = React.useState(null);

  // We find currently editing flow
  const flow = Array.isArray(flows) && selectedFlowId ? flows.find(f => f.id === selectedFlowId) : null;
  const setFlow = (updatedFlow) => {
    if (!updatedFlow) return;
    setFlows(flows.map(f => f.id === updatedFlow.id ? updatedFlow : f));
  };

  const patchFlow = (patch) => setFlow({ ...flow, ...patch });
  const patchStep = (si, patch) => {
    const steps = flow.steps.map((s, i) => (i === si ? { ...s, ...patch } : s));
    setFlow({ ...flow, steps });
  };
  const patchButton = (si, bi, patch) => {
    const steps = flow.steps.map((s, i) => {
      if (i !== si) return s;
      const buttons = (s.buttons || []).map((b, j) => (j === bi ? { ...b, ...patch } : b));
      return { ...s, buttons };
    });
    setFlow({ ...flow, steps });
  };

  const addStep = () => {
    const ids = new Set(flow.steps.map((s) => s.id));
    let n = flow.steps.length + 1;
    while (ids.has(slug(n))) n += 1;
    const id = slug(n);
    setFlow({ ...flow, steps: [...flow.steps, { id, body: '', is_final: false, buttons: [] }] });
  };
  const removeStep = (si) => {
    const removedId = flow.steps[si].id;
    const steps = flow.steps
      .filter((_, i) => i !== si)
      // null out any button still pointing at the removed step
      .map((s) => ({ ...s, buttons: (s.buttons || []).map((b) => (b.next === removedId ? { ...b, next: '' } : b)) }));
    const start_step = steps[0]?.id || '';
    setFlow({ ...flow, steps, start_step });
  };
  const addButton = (si) => {
    const s = flow.steps[si];
    if ((s.buttons || []).length >= 3) return;
    const id = `${s.id}_b${(s.buttons || []).length + 1}`;
    patchStep(si, { buttons: [...(s.buttons || []), { type: 'reply', id, title: '', next: '', add_tag: '', set: '', keywords: [] }] });
  };
  const MAX_LINKS = 5;
  const addLinkButton = (si) => {
    const s = flow.steps[si];
    const links = (s.buttons || []).filter((b) => b.type === 'url');
    if (links.length >= MAX_LINKS) return;
    const id = `${s.id}_link${links.length + 1}`;
    patchStep(si, { buttons: [...(s.buttons || []), { type: 'url', id, title: '', url: '' }] });
  };
  const removeButton = (si, bi) => {
    const s = flow.steps[si];
    patchStep(si, { buttons: (s.buttons || []).filter((_, j) => j !== bi) });
  };

  const handleToggleEnabled = (flowId, checked) => {
    setFlows(flows.map(f => f.id === flowId ? { ...f, enabled: checked } : f));
  };

  const handleDeleteFlow = (flowId) => {
    setFlows(flows.filter(f => f.id !== flowId));
  };

  const handleLoadTemplate = (template) => {
    const newId = `${template.id}_${Date.now()}`;
    const newFlow = {
      ...JSON.parse(JSON.stringify(template)),
      id: newId,
      name: `${template.name} (Copy)`
    };
    setFlows([...flows, newFlow]);
    setSelectedFlowId(newId);
  };

  const handleCreateBlank = () => {
    const newId = `flow_${Date.now()}`;
    const newFlow = {
      id: newId,
      name: 'New Custom Flow',
      enabled: false,
      trigger_keywords: [],
      start_step: 'step_1',
      expire_hours: 24,
      steps: [
        { id: 'step_1', body: 'Hello! Welcome to Reseller Bazzar. How can we help you today?', is_final: false, buttons: [] }
      ]
    };
    setFlows([...flows, newFlow]);
    setSelectedFlowId(newId);
  };

  // ---------- Flow List View ----------
  if (!selectedFlowId) {
    return (
      <Stack spacing={3}>
        {/* Top Header & Create Buttons */}
        <Card sx={{ p: 3 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontFamily: 'Outfit, sans-serif' }}>
                Interactive Automation Flows
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 500, fontFamily: 'Inter, sans-serif' }}>
                Unlike keyword replies, flows are stateful funnels that remember conversation context, ask questions, validate answers, and route buyers & sellers dynamically.
              </Typography>
            </Box>
            
            <Stack direction="row" spacing={1.5} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
              <Button 
                variant="contained" 
                startIcon={<Plus size={16} />} 
                onClick={handleCreateBlank}
              >
                Create Blank Flow
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2.5 }} />

          <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
            Quick Start Flow Templates
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between',
                  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.75),
                  '&:hover': { boxShadow: theme.shadows[1], borderColor: 'primary.main' }
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <Rocket size={15} /> Seller Onboarding
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
                    Qualifies leads, captures seller city & product category, and shares signup instructions.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => handleLoadTemplate(SELLER_ONBOARDING_TEMPLATE)}>
                  Load Template
                </Button>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between',
                  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.75),
                  '&:hover': { boxShadow: theme.shadows[1], borderColor: 'primary.main' }
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: 'secondary.main', display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <GitBranch size={15} /> Customer Acquisition
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
                    Onboards buyers/resellers, captures sourcing interests and budgets, and points to directory lists.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" color="secondary" onClick={() => handleLoadTemplate(CUSTOMER_ACQUISITION_TEMPLATE)}>
                  Load Template
                </Button>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card 
                variant="outlined" 
                sx={{ 
                  p: 2, 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  justifyContent: 'space-between',
                  borderColor: theme.palette.mode === 'dark' ? alpha(theme.palette.divider, 0.4) : alpha(theme.palette.divider, 0.75),
                  '&:hover': { boxShadow: theme.shadows[1], borderColor: 'primary.main' }
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 13.5, color: 'info.main', display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                    <MessageSquare size={15} /> General Enquiry
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 2 }}>
                    Handles customer support FAQs, processes callback slot requests, and routes logs.
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" color="info" onClick={() => handleLoadTemplate(GENERAL_ENQUIRY_TEMPLATE)}>
                  Load Template
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Card>

        {/* Existing Flows List */}
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: -1.5 }}>
          Your Configured Flows ({flows.length})
        </Typography>

        {flows.length === 0 ? (
          <Card sx={{ p: 4, textAlign: 'center', border: `1px dashed ${theme.palette.divider}`, borderRadius: 2.5 }}>
            <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
              No custom flows created yet. Load one of the quick start templates above to begin!
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {flows.map((f) => (
              <Grid size={12} key={f.id}>
                <Card 
                  sx={{ 
                    p: 2.5,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2.5,
                    background: theme.palette.mode === 'dark' 
                      ? alpha(theme.palette.background.paper, 0.8) 
                      : '#ffffff',
                    transition: 'all 0.2s',
                    '&:hover': { boxShadow: theme.shadows[2], borderColor: 'text.secondary' }
                  }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                    <Box sx={{ 
                      width: 44, 
                      height: 44, 
                      borderRadius: 2, 
                      bgcolor: f.enabled ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.text.disabled, 0.1),
                      color: f.enabled ? 'success.main' : 'text.disabled',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <GitBranch size={22} />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" sx={{ gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {f.name || 'Untitled Flow'}
                        </Typography>
                        <Chip 
                          label={`${f.steps ? f.steps.length : 0} Steps`} 
                          size="small" 
                          sx={{ fontWeight: 700, fontSize: 11, borderRadius: 1 }} 
                        />
                        <Chip 
                          label={`ID: ${f.id}`} 
                          size="small" 
                          variant="outlined" 
                          sx={{ fontSize: 10.5, borderRadius: 1 }} 
                        />
                      </Stack>

                      {/* Trigger keywords */}
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600 }}>Trigger Keywords:</Typography>
                        {f.trigger_keywords && f.trigger_keywords.length > 0 ? (
                          f.trigger_keywords.map((k) => (
                            <Chip 
                              key={k} 
                              label={k} 
                              size="small" 
                              sx={{ 
                                height: 20, 
                                fontSize: 11, 
                                fontWeight: 700, 
                                bgcolor: alpha(theme.palette.primary.main, 0.08), 
                                color: 'primary.main' 
                              }} 
                            />
                          ))
                        ) : (
                          <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontStyle: 'italic' }}>None (no keyword triggers)</Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Action Controls */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}>
                      <Stack alignItems="center" sx={{ mr: 1 }}>
                        <Switch 
                          checked={!!f.enabled} 
                          onChange={(e) => handleToggleEnabled(f.id, e.target.checked)} 
                          size="small"
                        />
                        <Typography sx={{ fontSize: 9.5, fontWeight: 800, color: f.enabled ? 'success.main' : 'text.disabled', mt: -0.25 }}>
                          {f.enabled ? 'LIVE' : 'OFF'}
                        </Typography>
                      </Stack>

                      <IconButton 
                        color="primary" 
                        onClick={() => setSelectedFlowId(f.id)} 
                        aria-label={`Edit flow ${f.name}`}
                        sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) } }}
                      >
                        <Pencil size={16} />
                      </IconButton>

                      <IconButton 
                        color="error" 
                        onClick={() => handleDeleteFlow(f.id)} 
                        aria-label={`Delete flow ${f.name}`}
                        sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), '&:hover': { bgcolor: alpha(theme.palette.error.main, 0.16) } }}
                      >
                        <Trash2 size={16} />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>
    );
  }

  const stepOptions = flow.steps.map((s) => s.id);

  // ---------- Editor ----------
  return (
    <Stack spacing={2}>
      {/* Back button and editor header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 1 }}>
        <Button 
          variant="outlined" 
          size="small" 
          startIcon={<ArrowLeft size={16} />} 
          onClick={() => setSelectedFlowId(null)}
          sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 700 }}
        >
          Back to Flows List
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          Editing Flow: {flow.name}
        </Typography>
      </Stack>

      {/* Flow details card */}
      <Card sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <GitBranch size={18} color={theme.palette.primary.main} />
              <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{flow.name || 'Onboarding flow'}</Typography>
            </Stack>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
              Starts when an inbound message matches a trigger keyword. The first step is the entry point.
            </Typography>
          </Box>
          <Stack alignItems="flex-end">
            <Switch checked={!!flow.enabled} onChange={(e) => patchFlow({ enabled: e.target.checked })} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: flow.enabled ? 'success.main' : 'text.disabled' }}>
              {flow.enabled ? 'Live' : 'Off'}
            </Typography>
          </Stack>
        </Stack>

        {flow.enabled && (
          <Alert severity="info" sx={{ mt: 1.5, fontSize: 12.5 }}>
            Buttons send only inside WhatsApp's 24-hour window and only when <code>WHATSAPP_LIVE_SEND=on</code> on the
            backend (otherwise sends are simulated). Deploy the backend before relying on this in production.
          </Alert>
        )}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <TextField size="small" label="Flow name" fullWidth value={flow.name || ''}
            onChange={(e) => patchFlow({ name: e.target.value })} />
          <TextField size="small" label="Session expiry (hours)" type="number" sx={{ width: { xs: '100%', sm: 190 } }}
            value={flow.expire_hours ?? 24} onChange={(e) => patchFlow({ expire_hours: Number(e.target.value) || 24 })} />
        </Stack>
        <TextField size="small" fullWidth sx={{ mt: 1.5 }} label="Trigger keywords (comma-separated)"
          placeholder="interested, intrested"
          helperText="The flow starts when an inbound message contains any of these (e.g. your broadcast's “Interested” button)."
          value={(flow.trigger_keywords || []).join(', ')}
          onChange={(e) => patchFlow({ trigger_keywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />

        {/* Re-engagement: pull stalled leads back into the funnel after the 24h window closes. */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 1.5 }}>
          <TextField size="small" fullWidth label="Re-engage template (approved Meta template name)"
            placeholder="seller_onboarding_reengage"
            helperText="Sent ONCE to a lead who stalls mid-funnel, to re-open WhatsApp's 24h window. Leave blank to disable. Must be an APPROVED template."
            value={flow.reengage_template || ''}
            onChange={(e) => patchFlow({ reengage_template: e.target.value.trim() })} />
          <TextField size="small" label="After (hours)" type="number" sx={{ width: { xs: '100%', sm: 150 } }}
            helperText="0 = off"
            value={flow.reengage_after_hours ?? 0}
            onChange={(e) => patchFlow({ reengage_after_hours: Number(e.target.value) || 0 })} />
        </Stack>
      </Card>

      {/* Steps */}
      {flow.steps.map((step, si) => (
        <Card key={step.id} sx={{ p: 2, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip size="small" label={si === 0 ? `START · ${step.id}` : step.id}
                sx={{ fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, si === 0 ? 0.14 : 0.06), color: 'primary.main' }} />
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Switch size="small" checked={!!step.is_final}
                  onChange={(e) => patchStep(si, { is_final: e.target.checked, buttons: e.target.checked ? (step.buttons || []).filter((b) => b.type === 'url') : step.buttons })} />
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Final step (sends message & ends)</Typography>
              </Stack>
            </Stack>
            {flow.steps.length > 1 && (
              <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => removeStep(si)} aria-label="Delete step">
                <Trash2 size={15} />
              </IconButton>
            )}
          </Stack>

          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
            <MessageSquare size={15} style={{ marginTop: 8 }} color={theme.palette.text.secondary} />
            <Box sx={{ flex: 1 }}>
              <WaMessageEditor value={step.body} minRows={2}
                placeholder="Message for this step — use *bold*, _italic_. Links become tappable."
                onChange={(v) => patchStep(si, { body: v })} />
            </Box>
          </Stack>

          {step.capture && (
            <Box sx={{ pl: 3 }}>
              <Alert severity="info" icon={<CornerDownRight size={16} />} sx={{ fontSize: 12.5 }}>
                Free-text step — waits for the lead's typed reply and saves it as <b>{step.capture}</b>
                {step.tag_prefix ? <> · tags <b>{step.tag_prefix}&lt;answer&gt;</b></> : null}
                {step.set_contact ? <> · stores it on the contact's <b>{step.set_contact}</b></> : null}
                , then goes to <b>{step.next || '— end —'}</b>.
              </Alert>
            </Box>
          )}

          {!step.capture && (() => {
            const hasReplyButtons = (step.buttons || []).some(b => b.type !== 'url');
            const hasLinkButton = (step.buttons || []).some(b => b.type === 'url');
            return (
              <Box sx={{ pl: 3 }}>
                {!step.is_final && hasReplyButtons && (
                  <>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
                      Reply buttons (max 3)
                    </Typography>
                    <Stack spacing={1.25}>
                      {(step.buttons || []).map((b, bi) => (
                        b.type === 'url' ? null :
                        <Card key={bi} sx={{ p: 1.5, bgcolor: theme.palette.background.neutral }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
                            <TextField size="small" label="Button label" sx={{ flex: 1 }} inputProps={{ maxLength: 20 }}
                              helperText={`${(b.title || '').length}/20`}
                              value={b.title || ''} onChange={(e) => patchButton(si, bi, { title: e.target.value })} />
                            <TextField select size="small" label="Goes to" sx={{ width: { xs: '100%', md: 170 } }}
                              value={b.next || ''} onChange={(e) => patchButton(si, bi, { next: e.target.value })}
                              InputProps={{ startAdornment: <CornerDownRight size={14} style={{ marginRight: 6, opacity: 0.6 }} /> }}>
                              <MenuItem value="">— End flow —</MenuItem>
                              {stepOptions.filter((id) => id !== step.id).map((id) => <MenuItem key={id} value={id}>{id}</MenuItem>)}
                            </TextField>
                            <TextField size="small" label="Tag to add" sx={{ width: { xs: '100%', md: 160 } }}
                              placeholder="Lead: Seller"
                              value={b.add_tag || ''} onChange={(e) => patchButton(si, bi, { add_tag: e.target.value })}
                              InputProps={{ startAdornment: <Tag size={13} style={{ marginRight: 6, opacity: 0.6 }} /> }} />
                            <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => removeButton(si, bi)} aria-label="Delete button">
                              <Trash2 size={14} />
                            </IconButton>
                          </Stack>
                          <TextField size="small" fullWidth sx={{ mt: 1 }} label="Also match if they type (comma-separated synonyms)"
                            placeholder="yes, online, amazon"
                            value={(b.keywords || []).join(', ')}
                            onChange={(e) => patchButton(si, bi, { keywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                        </Card>
                      ))}
                    </Stack>
                  </>
                )}

                {hasLinkButton && (
                  <>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                      Link buttons (up to {MAX_LINKS})
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mb: 1 }}>
                      WhatsApp shows only one styled button, so the <b>first</b> link becomes the tap button; any extras
                      are added as tappable links at the bottom of the message.
                    </Typography>
                    <Stack spacing={1.25}>
                      {(step.buttons || []).map((b, bi) => (
                        b.type !== 'url' ? null : (() => {
                        const isPrimaryLink = (step.buttons || []).findIndex((x) => x.type === 'url') === bi;
                        return (
                        <Card key={bi} sx={{ p: 1.5, bgcolor: theme.palette.background.neutral }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ md: 'center' }}>
                            <TextField size="small" label={isPrimaryLink ? 'Button label (tap button)' : 'Link label'}
                              sx={{ width: { xs: '100%', md: 240 } }} inputProps={{ maxLength: isPrimaryLink ? 20 : 40 }}
                              helperText={isPrimaryLink ? `${(b.title || '').length}/20 · styled button` : 'shown as a text link'}
                              value={b.title || ''} onChange={(e) => patchButton(si, bi, { title: e.target.value })} />
                            <TextField size="small" label="Link URL" sx={{ flex: 1 }}
                              placeholder="https://example.com/signup"
                              value={b.url || ''} onChange={(e) => patchButton(si, bi, { url: e.target.value })} />
                            <IconButton size="small" sx={{ color: 'error.main' }} onClick={() => removeButton(si, bi)} aria-label="Delete link">
                              <Trash2 size={14} />
                            </IconButton>
                          </Stack>
                        </Card>
                        ); })()
                      ))}
                    </Stack>
                  </>
                )}

                <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                  {!step.is_final && !hasLinkButton && (step.buttons || []).filter((b) => b.type !== 'url').length < 3 && (
                    <Button size="small" startIcon={<Plus size={15} />} onClick={() => addButton(si)}>
                      Add button
                    </Button>
                  )}
                  {!hasReplyButtons && (step.buttons || []).filter((b) => b.type === 'url').length < MAX_LINKS && (
                    <Button size="small" startIcon={<Link size={15} />} onClick={() => addLinkButton(si)}>
                      {hasLinkButton ? 'Add another link' : 'Add link'}
                    </Button>
                  )}
                </Stack>
              </Box>
            );
          })()}
        </Card>
      ))}

      <Stack direction="row" justifyContent="space-between">
        <Button variant="outlined" startIcon={<Plus size={16} />} onClick={addStep}>Add step</Button>
        <Button 
          color="error" 
          startIcon={<Trash2 size={16} />} 
          onClick={() => {
            setFlows(flows.filter((f) => f.id !== flow.id));
            setSelectedFlowId(null);
          }}
        >
          Delete flow
        </Button>
      </Stack>

      <Divider />
      <Typography sx={{ fontSize: 11.5, color: 'text.disabled' }}>
        Tip: keep button labels short (WhatsApp caps them at 20 characters). “Goes to” chains steps together; choose
        “End flow” on the final answer, or mark a step as <b>Final</b> to send a closing message (e.g. the signup link).
      </Typography>
    </Stack>
  );
};

export default WhatsAppFlowBuilder;
